import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { type Job } from '@marketplace/contracts';

/**
 * What the job is, as its owner posted it — trades, place, budget, urgency. Operator, 2026-10-01: a
 * job's page should *"clearly report the associated labels"*; until now the client's page showed
 * only a title and a status. The provider's view already says these through `FeedJobFacts`.
 *
 * A licensed trade is marked as one: the same plain statement the feed and quote coverage make.
 */
export function JobFacts({ job, locale }: { job: Job; locale: string }): ReactElement {
  const { t } = useTranslation();
  const english = locale.startsWith('en');
  const money = (cents: number): string =>
    (cents / 100).toLocaleString(locale, { style: 'currency', currency: 'EUR' });

  const { minCents, maxCents } = job.budget;
  const budget =
    minCents === null && maxCents === null
      ? null
      : minCents !== null && maxCents !== null
        ? `${money(minCents)} – ${money(maxCents)}`
        : minCents === null
          ? t('feed.budget.upTo', { max: money(maxCents as number) })
          : t('feed.budget.from', { min: money(minCents) });

  return (
    <section className="mp-section" aria-label={t('job.facts.label')} data-testid="job-facts">
      {job.categories.length === 0 ? null : (
        <>
          <p className="mp-meta">{t('job.facts.trades')}</p>
          <ul className="mp-list" data-testid="job-trades">
            {job.categories.map((category) => (
              <li key={category.slug}>
                <strong>{english ? category.nameEn : category.nameEs}</strong>
                {category.requiresLicence ? ` · ${t('feed.coverage.licence')}` : ''}
              </li>
            ))}
          </ul>
        </>
      )}
      {job.location === null ? null : (
        <p className="mp-meta">
          {t('job.facts.where', {
            place: `${job.location.city}, ${job.location.province} (${job.location.postalCode})`,
          })}
        </p>
      )}
      {budget === null ? null : <p className="mp-meta">{t('feed.budget', { range: budget })}</p>}
      {job.urgency === null ? null : (
        <p className="mp-meta">
          {t('job.facts.urgency', { when: t(`search.when.${job.urgency}`) })}
        </p>
      )}
    </section>
  );
}
